import { useEffect, useMemo, useState } from 'react';
import { Badge, DataTable, InlineStack, Link, Modal, SkeletonBodyText, Text } from '@shopify/polaris';
import { formatToCurrency, getRequest } from '../../../../shared/components/functions';
import { MONTH_LABELS, formatCompactCurrency, formatInteger, formatPercent, formatShortDate, periodToQueryParams, sumBy, toNumber } from './analyticsHelpers';

const MAX_COMPANY_ROWS = 100;

export function formatDrillPeriodLabel(period) {
  if (!period) return '';
  if (period.month) return `${MONTH_LABELS[period.month - 1]} ${period.year}`;
  if (period.quarter) return `Q${period.quarter} ${period.year}`;
  if (period.half) return `H${period.half} ${period.year}`;
  if (period.through_month) return `Jan–${MONTH_LABELS[period.through_month - 1]} ${period.year}`;
  return String(period.year);
}

// Drill-down for one clicked period: who the money came from / went to,
// plus the largest individual invoices in that period.
export default function PeriodDrilldownModal({ period, onClose, config, businessUnitId = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!period) return;
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      setData(null);
      const params = periodToQueryParams(period);
      if (businessUnitId) params.append('businessUnitId', businessUnitId);
      const result = await getRequest(`${config.endpoint}?${params}`);
      if (!cancelled) {
        setData(result);
        setLoading(false);
      }
    }
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [period, config.endpoint, businessUnitId]);

  const byCompany = useMemo(() => (Array.isArray(data?.byCompany) ? data.byCompany : []), [data]);
  const largestInvoices = Array.isArray(data?.largestInvoices) ? data.largestInvoices : [];
  const creditNotes = data?.creditNotes;
  const creditNoteCount = toNumber(creditNotes?.credit_note_count);
  const grandTotal = sumBy(byCompany, 'total_incl');
  const showCredited = !!config.showCreditedColumn;

  let cumulative = 0;
  const companyRows = byCompany.slice(0, MAX_COMPANY_ROWS).map((row, idx) => {
    const total = toNumber(row.total_incl);
    const credited = toNumber(row.credited_incl);
    cumulative += total;
    return [
      idx + 1,
      <Link removeUnderline url={`/companies/${row.company_id}`}>
        {String(row.company_name || 'Unknown').substring(0, 40)}
      </Link>,
      formatInteger(row.invoice_count),
      formatToCurrency(total),
      ...(showCredited
        ? [
            credited > 0 ? (
              <Text tone="critical" as="span">
                −{formatToCurrency(credited)}
              </Text>
            ) : (
              '—'
            )
          ]
        : []),
      grandTotal ? formatPercent(total / grandTotal) : '—',
      grandTotal ? formatPercent(cumulative / grandTotal) : '—',
      formatToCurrency(toNumber(row.open_amount))
    ];
  });

  const invoiceRows = largestInvoices.map((row) => [
    <InlineStack gap="200" blockAlign="center" wrap={false}>
      <Link removeUnderline url={`${config.invoiceLinkBase}/${row.id}`}>
        {row.invoice_number || row.reference || row.id}
      </Link>
      {toNumber(row.credit_note_count) > 0 ? <Badge tone="critical">{`credited −${formatCompactCurrency(row.credited_incl)}`}</Badge> : null}
    </InlineStack>,
    <Link removeUnderline url={`/companies/${row.company_id}`}>
      {String(row.company_name || 'Unknown').substring(0, 36)}
    </Link>,
    formatShortDate(row.datum),
    formatToCurrency(toNumber(row.total_incl)),
    formatToCurrency(toNumber(row.open_amount))
  ]);

  return (
    <Modal size="large" open={!!period} onClose={onClose} title={`${config.titlePrefix} — ${formatDrillPeriodLabel(period)}`}>
      <Modal.Section>
        {loading || !data ? (
          <SkeletonBodyText lines={10} />
        ) : (
          <>
            <Text tone="subdued" as="p">
              {formatInteger(byCompany.length)} {config.entityLabel} · {formatInteger(sumBy(byCompany, 'invoice_count'))} invoices · {formatToCurrency(grandTotal)} incl. VAT
              {byCompany.length > MAX_COMPANY_ROWS ? ` · showing the top ${MAX_COMPANY_ROWS}` : ''}
            </Text>
            {creditNoteCount > 0 ? (
              <Text tone="subdued" as="p" variant="bodySm">
                Heads up: {formatInteger(creditNoteCount)} credit note{creditNoteCount === 1 ? '' : 's'} dated in this period totaling −{formatToCurrency(toNumber(creditNotes.total_incl))} incl. VAT.
                The invoice amounts below are gross; the heatmap and period tables are net of credit notes.
              </Text>
            ) : null}
            <div style={{ marginTop: '12px' }}>
              <DataTable
                verticalAlign="middle"
                increasedTableDensity
                stickyHeader
                columnContentTypes={['numeric', 'text', 'numeric', 'numeric', ...(showCredited ? ['numeric'] : []), 'numeric', 'numeric', 'numeric']}
                headings={[
                  <b>#</b>,
                  <b>{config.entityHeading}</b>,
                  <b>Invoices</b>,
                  <b>Total (incl. VAT)</b>,
                  ...(showCredited ? [<b>Credited</b>] : []),
                  <b>Share</b>,
                  <b>Cumulative</b>,
                  <b>Open</b>
                ]}
                rows={companyRows}
              />
            </div>
            <div style={{ marginTop: '20px' }}>
              <Text variant="headingSm" as="h3">
                Largest invoices in this period
              </Text>
              <DataTable
                verticalAlign="middle"
                increasedTableDensity
                columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric']}
                headings={[<b>Number</b>, <b>{config.entityHeading}</b>, <b>Date</b>, <b>Total (incl. VAT)</b>, <b>Open</b>]}
                rows={invoiceRows}
              />
            </div>
          </>
        )}
      </Modal.Section>
    </Modal>
  );
}
